import React, {useState, useEffect, useRef} from 'react';
import {ScrollView, View, Text, Pressable, Modal, Image} from 'react-native';
import {useToast} from 'react-native-toast-notifications';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import DynamicTextBox from '../DynamicControls/DynamicTextBox';
import DynamicRecipients from '../DynamicControls/DynamicRecipients';
import DynamicAutocomplete from '../DynamicControls/DynamicAutocomplete';
import DynamicBundleLinkRecord from '../DynamicControls/DynamicBundleLinkRecord';
import Icon from '../Shared/Icon';
import LoadingSpinner from '../Shared/LoadingSpinner';
import {ObjectId, UUID} from 'bson';
import {MongoIdSqlId} from '../../Types/DtoTypes';
import {
  SubmissionBundle,
  SubmissionBundle_submissions,
  Submission,
} from '../../Models/PowerSyncModels/models';
import {ListDataTypes} from '../../Constants/ListDataTypes';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {DynamicPageStyleSheet} from '../../Styles/DynamicPageStyles';
import Colors from '../../Styles/Shared/Colors';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {
  FORM_TYPE,
  METADATA_KEYS,
  PERMISSIONS,
} from '../../Constants/AppConstants';
import {usePermission} from '../../Providers/PermissionProvider';
import {AxiosRequestConfig} from 'axios';
import ApiService from '../../Services/ApiService';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import DynamicAttachments from '../DynamicControls/DynamicAttachments';
import DynamicAddRecord from '../DynamicControls/DynamicAddRecord';
import {TemplateTypes} from '../../Constants/TemplateCategoryTypes';
import DynamicCheckBox from '../DynamicControls/DynamicCheckBox';
import DynamicSegmentedControl from '../DynamicControls/DynamicSegmentedControl';
import DynamicPDF, {FileUrl} from '../DynamicForm/DynamicPDF';
import DynamicBundlePDF from '../DynamicForm/DynamicBundlePDF';
import {DynamicAttachment} from '../../Types/ControlTypes';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import {ILogService} from '../../Services/Interfaces/ILogService';
import LogService from '../../Services/LogService';
import {Images} from '../../Constants/Images';
import JSZip from 'jszip';
import {saveAs} from 'file-saver';

type EditSubmissionBundleProps = {
  navigation: any;
  bundleId?: string;
};

const EditSubmissionBundle = (
  props: EditSubmissionBundleProps,
): React.ReactElement => {
  const {
    submissions,
    submissionBundles,
    organisations,
    people,
    orgContractorNotificationRecipients,
    templates,
    templateVersions,
    submissionTrainings,
    getSubmissionBundle,
  } = usePowerSyncData();

  const {createSubmissionBundle, updateSubmissionBundle, deleteItems} =
    usePowerSyncWriteData();
  const user = usePowerSyncUser();
  const orgId = user.getUserInfo().orgId.toString();
  const toast = useToast();

  const [bundle, setBundle] = useState<SubmissionBundle | undefined>(undefined);
  const [SQLServerId, setSQLServerId] = useState('');
  const [isExternal, setIsExternal] = useState(false);
  const [externalOrgValue, setExternalOrgValue] = useState('');

  const [nameError, setNameError] = useState(true);
  const [documentsError, setDocumentsError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const {hasPermission} = usePermission();
  const [canSubmitExternal, setCanSubmitExternal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [auditError, setAuditError] = useState('');
  const [submissionError, setSubmissionError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isShowingPDFModal, setIsShowingPDFModal] = useState(false);

  const [isShowingActionsModal, setIsShowingActionsModal] = useState(false);
  const [attachmentsMounted, setAttachmentsMounted] = useState(false);

  const urlsRef = useRef<FileUrl[]>([]);

  const logService: ILogService = new LogService();

  useEffect(() => {
    let submitExternal = hasPermission(PERMISSIONS.BUNDLE_EXTERNAL_SUBMISSION);
    setCanSubmitExternal(submitExternal);
  }, []);

  useEffect(() => {
    const loadBundle = async () => {
      let realmBundle = await getSubmissionBundle(props.bundleId!);

      if (!realmBundle) {
        goBack();
        return;
      }

      setNameError(false);

      if (realmBundle.partition !== orgId) setIsExternal(true);

      let externalOrg = realmBundle.externalOrgId;
      if (externalOrg) {
        let orgArray = organisations;
        let orgObj = orgArray.find(o => o.SQLServerId === externalOrg);
        if (orgObj)
          setExternalOrgValue(
            JSON.stringify({
              mongoId: orgObj._id,
              label: orgObj.name + (!orgObj.isActive ? ' - Inactive' : ''),
              SQLServerId: orgObj.SQLServerId,
            } as MongoIdSqlId),
          );
      }

      setBundle(realmBundle);
      setSQLServerId(realmBundle.SQLServerId ?? new UUID().toString());
      setIsLoading(false);
    };

    loadBundle();
  }, [props.bundleId]);

  const showFilePickerCommand = useRef({
    showFilePicker: () => {},
    setShowFilePicker(func: () => void) {
      this.showFilePicker = func;
    },
  });

  const openDocumentLibraryCommand = useRef({
    openDocumentLibrary: () => {},
    setOpenDocumentLibrary(func: () => void) {
      this.openDocumentLibrary = func;
    },
  });

  const showDocumentsModalCommand = useRef({
    showDocumentsModal: () => {},
    setShowDocumentsModal(func: () => void) {
      this.showDocumentsModal = func;
    },
  });

  const addUrls = (urls: FileUrl[]) => {
    let newUrlsRef = [...urlsRef.current];

    let index = urls.length > 0 ? urls[0].index : undefined;
    if (index) {
      newUrlsRef = newUrlsRef.filter(x => x.index != index);
      newUrlsRef.push(...urls);
      urlsRef.current = newUrlsRef;
    }
  };

  const downloadZip = async () => {
    setIsLoading(true);

    let zip = new JSZip();
    let zipNames: string[] = [];

    let urls = [...urlsRef.current];

    let remoteFiles = urls.map(async url => {
      let response = await fetch(url.url);
      let data = await response.blob();

      let filename = url.name;

      let isUniqueName = false;
      let index = 0;
      while (isUniqueName == false) {
        if (zipNames.some(x => x == filename)) {
          index += 1;
          let namePart = url.name.split('.')[0];
          let extensionPart = url.name.split('.')[1];

          filename = namePart + '_' + index + '.' + extensionPart;
        } else {
          isUniqueName = true;
          zipNames.push(filename);
        }
      }

      zip.file(filename, data);
    });

    await Promise.all(remoteFiles);

    let content = await zip.generateAsync({type: 'blob'});

    saveAs(content, (bundle?.name ?? 'Bundle') + '.zip');

    setIsLoading(false);
  };

  const goBack = () => {
    props.navigation.pop();
  };

  const showPDFModal = () => {
    setIsShowingActionsModal(false);
    setIsShowingPDFModal(true);
  };

  async function onChange(
    propName: string,
    propValue: string,
    isValid: boolean,
  ): Promise<void> {
    if (!bundle || bundle.status === SubmissionStatuses.SUBMITTED) return;

    let newBundle = {...bundle} as SubmissionBundle;

    if (propName === 'name') {
      newBundle.name = propValue;
      setNameError(!isValid);
    } else if (propName === 'internalRecipients') {
      let internalRecipients: string[] = [];

      if (propValue) internalRecipients = JSON.parse(propValue) as string[];
      newBundle.internalRecipients = internalRecipients;
      setSubmissionError('');
    } else if (propName === 'externalOrgId') {
      if (propValue) {
        let valObj = JSON.parse(propValue) as MongoIdSqlId;
        newBundle.externalOrgId = valObj.SQLServerId;
      } else newBundle.externalOrgId = '';

      setExternalOrgValue(propValue);
    } else if (propName === 'externalRecipients') {
      let externalRecipients: string[] = [];

      if (propValue) externalRecipients = JSON.parse(propValue) as string[];
      newBundle.externalRecipients = externalRecipients;
      setSubmissionError('');
    } else if (propName === 'auditSubmissionId') {
      if (propValue) newBundle.auditSubmissionId = propValue;
      else newBundle.auditSubmissionId = undefined;
      setSubmissionError('');
    } else if (propName === 'isAudit') {
      if (propValue == 'Yes') newBundle.isAudit = true;
      else {
        newBundle.isAudit = false;
        setAuditError('');
        newBundle.auditSubmissionId = undefined;
      }
    } else if (propName === 'submissions') {
      setDocumentsError('');

      let realmBundle = await getSubmissionBundle(props.bundleId!);

      if (realmBundle) newBundle = realmBundle;
    } else if (propName === 'attachments') {
      setDocumentsError('');

      if (attachmentsMounted) {
        newBundle.bundleAttachments = propValue;
        if (!newBundle.SQLServerId) newBundle.SQLServerId = SQLServerId;
      } else if (newBundle.bundleAttachments == propValue)
        setAttachmentsMounted(true);
    }

    setBundle(newBundle);

    if (JSON.stringify(newBundle) !== JSON.stringify(bundle)) {
      newBundle.dataHubVersion = Math.round(Date.now() / 1000);

      updateSubmissionBundle(newBundle);
    }
  }

  const copyAuditSubmission = (
    auditSubmission: Submission,
  ): SubmissionBundle_submissions => {
    let auditSubmissionAnswer = {
      submissionId: bundle?.auditSubmissionId,
      answers: auditSubmission.answers.map(a => ({
        answer: a.answer,
        controlId: a.controlId,
        controlTypeId: a.controlTypeId,
      })),
      metadataJSON: auditSubmission.metadataJSON,
      templateType: {
        _id: auditSubmission.templateType._id,
        name: auditSubmission.templateType.name,
      },
      templateVersion: {
        _id: auditSubmission.templateVersion._id,
        name: auditSubmission.templateVersion.name,
      },
      updatedBy: auditSubmission.updatedBy,
      updateDateTimeStamp: new Date(),
    } as SubmissionBundle_submissions;

    return auditSubmissionAnswer;
  };

  const onSubmit = async () => {
    setShowErrors(true);

    let auditErrorFlag = false;
    if (bundle && bundle.isAudit) {
      let submission = submissions.find(
        x => x._id === bundle.auditSubmissionId,
      );
      if (
        !submission ||
        submission.submissionStatus.name != SubmissionStatuses.SUBMITTED
      )
        auditErrorFlag = true;
    }

    if (auditErrorFlag) setAuditError('Complete Audit Header required');
    else setAuditError('');

    if (
      nameError ||
      auditErrorFlag ||
      !bundle ||
      bundle.status === SubmissionStatuses.SUBMITTED
    )
      return;

    if (
      (!bundle.internalRecipients || bundle.internalRecipients?.length == 0) &&
      (!bundle.externalRecipients || bundle.externalRecipients?.length == 0)
    ) {
      setSubmissionError('At least one recipient is required for submission');
      return;
    }

    if (
      (!bundle.submissions || bundle.submissions?.length == 0) &&
      (!bundle.bundleAttachments ||
        bundle.bundleAttachments == '' ||
        bundle.bundleAttachments == '[]')
    ) {
      setDocumentsError(
        'You need to add a document or an attachment for submission',
      );
      return;
    }

    // Don't submit if external org has been selected and ext recipients is empty
    if (
      bundle.externalOrgId &&
      (!bundle.externalRecipients || bundle.externalRecipients.length === 0)
    )
      return;

    if (bundle.isAudit) {
      if (bundle.auditSubmissionId) {
        let auditSubmission = submissions.find(
          x => x._id == bundle.auditSubmissionId,
        );

        //Check it exists / submitted
        if (
          !auditSubmission ||
          auditSubmission.submissionStatus.name !== SubmissionStatuses.SUBMITTED
        ) {
          setAuditError('Complete Audit header required');
          return;
        }
      } else {
        setAuditError('Complete Audit header required');
        return;
      }
    }

    setShowConfirmModal(true);
  };

  const submitBundle = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      //Copy audit submission so external users can view it
      if (bundle && bundle.auditSubmissionId && bundle.isAudit) {
        let auditSubmission = submissions.find(
          x => x._id == bundle.auditSubmissionId,
        );
        let auditSubmissionCopy = copyAuditSubmission(auditSubmission!);
        bundle.submissions?.push(auditSubmissionCopy);
        await updateSubmissionBundle(bundle);
      }

      let emailStatus = await sendSubmissionEmail();
      if (!emailStatus) {
        setIsLoading(false);
        return;
      }

      let newBundle = {...bundle} as SubmissionBundle;
      newBundle.status = SubmissionStatuses.SUBMITTED;
      newBundle.submitDateTimeStamp = new Date();
      await updateSubmissionBundle(newBundle);

      toast.show('Bundle submitted successfully', {
        type: 'success',
      });
      goBack();
    } catch (error) {
      toast.show('Submission Bundle has failed, please try again later', {
        type: 'danger',
      });
      await logService.sendLog(JSON.stringify(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onClose = async () => {
    // If bundle is empty, then delete it
    if (
      bundle &&
      !bundle.name &&
      (!bundle.internalRecipients || bundle.internalRecipients.length === 0) &&
      !bundle.externalOrgId &&
      (!bundle.externalRecipients || bundle.externalRecipients.length === 0)
    )
      await deleteItems([bundle._id], 'SubmissionBundles');

    goBack();
  };

  async function getDocumentsAndAttachmentsTitles(): Promise<string[]> {
    let docTitles: string[] = [];

    let filteredTemplates = templates;
    let filteredVersions = templateVersions;

    bundle!.submissions?.forEach(s => {
      let version = filteredVersions.find(
        v => v._id === s.templateVersion?._id,
      );
      let template = filteredTemplates.find(t => t._id === version?.templateId);
      let title = template?.name ?? '';

      if (s.metadataJSON) {
        let meta = JSON.parse(s.metadataJSON);
        if (meta[METADATA_KEYS.TITLE]) title = meta[METADATA_KEYS.TITLE];
      }

      if (template) {
        if (s.templateType?.name === 'Training Breakdown') {
          let training = submissionTrainings.find(
            t => s.submissionId && t._id === s.submissionId,
          );

          if (training && training.trainingType) {
            let category = training.trainingType.name
              ? 'Training'
              : 'Certificate';
            title =
              (category === 'Training'
                ? training.trainingType!.name
                : training.certificateType!.name) +
              ' - ' +
              training.person!.firstName +
              ' ' +
              training.person!.lastName;
          }
        }
      }

      docTitles.push(title);
    });

    if (bundle!.bundleAttachments) {
      try {
        let attachmentsArray = JSON.parse(
          bundle!.bundleAttachments,
        ) as Array<DynamicAttachment>;

        attachmentsArray.forEach(a => {
          if (a.name) docTitles.push(a.name);
        });
      } catch (error) {}
    }

    return docTitles;
  }

  const sendSubmissionEmail = async (): Promise<boolean> => {
    let submissionEmailUrl = process.env.REACT_APP_SUBMISSION_EMAIL_URL;
    if (!user) return false;

    let emailInternalRecipients = people
      .filter(
        x => x.email && bundle?.internalRecipients?.indexOf(x.email) != -1,
      )
      .map(y => ({
        Name: y.firstName + ' ' + y.lastName,
        Email: y.email,
      }));

    let emailExternalRecipients = orgContractorNotificationRecipients
      .filter(
        x =>
          bundle?.externalRecipients &&
          x.email &&
          bundle?.externalRecipients?.indexOf(x.email) != -1,
      )
      .map(y => ({
        Name: y.firstName + ' ' + y.lastName,
        Email: y.email,
      }));

    let config: AxiosRequestConfig = {};
    config.data = {
      OrgName: user.getUserInfo().orgName,
      OrgId: parseInt(orgId),
      ExternalOrgId:
        bundle?.externalOrgId != null ? parseInt(bundle.externalOrgId) : null,
      InternalRecipients: emailInternalRecipients,
      ExternalRecipients: emailExternalRecipients,
      SubmitterName: user.getUserInfo().personName,
      SubmitterEmail: user.getUserInfo().email,
      ReportTitles: await getDocumentsAndAttachmentsTitles(),
      SubmissionId: bundle!._id!,
      SubmitTitle: bundle!.name,
      SubmitDate: bundle!.createDateTimeStamp,
    };
    config.method = 'POST';
    config.url = submissionEmailUrl;
    config.headers = {'Content-Type': 'application/json'};
    let apiService = new ApiService(() => {});
    let response = await apiService
      .sendRequest<any>(config)
      .then(
        (response: any) => {
          return true;
        },
        error => {
          console.log(error);
          return false;
        },
      )
      .catch(function (error) {
        return false;
      });

    return response;
  };

  const renderConfirmModal = (): React.ReactElement => {
    return (
      <Modal
        visible={showConfirmModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View
            style={[ControlsStyleSheet.groupSelectorModal, {height: 'auto'}]}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text
                style={[
                  ControlsStyleSheet.groupSelectorModalTitle,
                  {fontSize: 15},
                ]}>
                Document Bundle Submission Confirmation
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={() => setShowConfirmModal(false)}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <ScrollView
              keyboardShouldPersistTaps='handled'
              style={{margin: 20}}>
              <View style={{}}>
                <Text
                  style={[
                    ControlsStyleSheet.label,
                    {
                      fontSize: 15,
                      textTransform: 'none',
                    },
                  ]}>
                  You are submitting a document bundle to internal and external
                  recipients. Your recipients will be notified by email and they
                  will then be able to view the documents in your submission.
                  Once this bundle has been submitted it cannot be deleted and
                  the documents it contains cannot be modified.
                </Text>
                <Text
                  style={[
                    ControlsStyleSheet.label,
                    {fontSize: 15, textTransform: 'none'},
                  ]}>
                  Do you want to submit this document bundle now?
                </Text>
              </View>
            </ScrollView>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingVertical: 20,
              }}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.whiteButton,
                  {alignSelf: 'center', marginTop: 0, marginBottom: 20},
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => setShowConfirmModal(false)}>
                <Text style={CommonStyleSheet.whiteButtonText}>No</Text>
              </Pressable>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.greenButton,
                  {alignSelf: 'center', marginTop: 0, marginBottom: 20},
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={() => submitBundle()}>
                <Text style={CommonStyleSheet.greenButtonText}>Yes</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderPDFModal = (): React.ReactElement => {
    if (!bundle) return <></>;

    let submissionsInBundle: {
      id: string;
      type: 'submission' | 'submissionTraining';
    }[] = [];

    if (bundle.submissions)
      bundle.submissions.forEach(s => {
        if (s.submissionId) {
          submissionsInBundle.push({
            id: s.submissionId,
            type:
              s.templateType?.name == TemplateTypes.TRAINING_BREAKDOWN
                ? 'submissionTraining'
                : 'submission',
          });
        }
      });

    return (
      <Modal
        visible={isShowingPDFModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={ControlsStyleSheet.groupSelectorModal}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                Download Documents
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={() => setIsShowingPDFModal(false)}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <ScrollView
              keyboardShouldPersistTaps='handled'
              style={{marginVertical: 24}}>
              <View style={ControlsStyleSheet.attachmentsContainer}>
                {isShowingPDFModal && (
                  <>
                    <View
                      style={[
                        ControlsStyleSheet.attachmentsItem,
                        {backgroundColor: Colors.green},
                      ]}>
                      <Icon icon={'zip'} color={Colors.white} size={20} />
                      <Pressable
                        style={({pressed}) => [
                          pressed && {
                            opacity: 0.6,
                          },
                        ]}
                        onPress={() => downloadZip()}>
                        <Text
                          style={[
                            ControlsStyleSheet.attachmentsText,
                            {
                              color: Colors.white,
                            },
                          ]}>
                          Download all to zip folder
                        </Text>
                      </Pressable>
                    </View>
                    <DynamicBundlePDF
                      bundleId={bundle!._id}
                      index={1}
                      addUrls={urls => addUrls(urls)}
                    />
                    {bundle.isAudit &&
                      bundle.auditSubmissionId &&
                      !bundle.submissions?.some(
                        x => x.submissionId! === bundle.auditSubmissionId!,
                      ) && (
                        <DynamicPDF
                          submissionId={bundle.auditSubmissionId}
                          type={'submission'}
                          index={2}
                          addUrls={urls => addUrls(urls)}
                        />
                      )}
                    {submissionsInBundle.map((s, i) => {
                      return (
                        <DynamicPDF
                          key={s.id}
                          submissionId={s.id}
                          type={s.type}
                          bundleId={bundle!._id}
                          index={i + 3}
                          addUrls={urls => addUrls(urls)}
                        />
                      );
                    })}
                  </>
                )}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  const renderActionsModal = (): React.ReactElement => {
    return (
      <Modal
        visible={isShowingActionsModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <Pressable
          style={[
            ControlsStyleSheet.modalBackground,
            {
              //@ts-ignore
              cursor: 'auto',
            },
          ]}
          onPress={() => setIsShowingActionsModal(false)}>
          <View
            style={{
              backgroundColor: Colors.white,
              alignSelf: 'flex-end',
              borderRadius: 20,
              marginTop: 110,
              marginRight: 10,
              paddingHorizontal: 20,
            }}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                More Actions
              </Text>
            </View>
            <View style={{marginVertical: 20}}>
              <Pressable
                style={({pressed}) => [
                  CommonStyleSheet.greenButton,
                  {alignSelf: 'center', marginTop: 12},
                  pressed && {
                    opacity: 0.8,
                  },
                ]}
                onPress={showPDFModal}>
                <Text style={CommonStyleSheet.greenButtonText}>
                  Generate PDF
                </Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
    );
  };

  return (
    <View style={DynamicPageStyleSheet.bodyContainer}>
      <View style={DynamicPageStyleSheet.header}>
        <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
          <Text style={DynamicPageStyleSheet.headerText}>
            Submission Bundle
          </Text>
          <View style={{paddingRight: 10, justifyContent: 'flex-start'}}>
            <Pressable
              style={({pressed}) => [
                pressed && {
                  opacity: 0.4,
                },
              ]}
              onPress={onClose}>
              <Icon icon={'close-circle'} color={Colors.white} size={24} />
            </Pressable>
            <Pressable
              style={({pressed}) => [
                {marginTop: 24, marginBottom: 30},
                pressed && {
                  opacity: 0.4,
                },
              ]}
              onPress={() => setIsShowingActionsModal(true)}>
              <Icon icon={'more'} color={Colors.white} size={20} />
            </Pressable>
          </View>
        </View>
      </View>
      <ScrollView style={DynamicPageStyleSheet.body}>
        <View style={DynamicPageStyleSheet.controlsContainer}>
          {bundle && (
            <React.Fragment>
              <DynamicTextBox
                label={'Submission Bundle Title'}
                config={{
                  required: true,
                  maxLength: 100,
                }}
                value={bundle.name}
                onChange={(controlId, controlTypeId, value, isValid) =>
                  onChange('name', value, isValid)
                }
                showError={showErrors}
                disabled={bundle.status === SubmissionStatuses.SUBMITTED}
              />
              <DynamicSegmentedControl
                config={{segments: [{name: 'Yes'}, {name: 'No'}]}}
                label='Is this an Audit Submission Bundle?'
                value={bundle.isAudit ? 'Yes' : 'No'}
                disabled={bundle.status === SubmissionStatuses.SUBMITTED}
                onChange={(controlId, controlTypeId, value, isValid) =>
                  onChange('isAudit', value, isValid)
                }
              />
              <DynamicAddRecord
                value={bundle.auditSubmissionId}
                label={'Audit Header'}
                config={{
                  templateType: TemplateTypes.AUDIT,
                }}
                disabled={bundle.status === SubmissionStatuses.SUBMITTED}
                isExternal={isExternal}
                bundleId={bundle._id}
                type={FORM_TYPE.SUBMISSION}
                onRecordAdded={submissionId => {
                  onChange('auditSubmissionId', submissionId, true);
                }}
                removeRecord={() => {
                  onChange('auditSubmissionId', '', true);
                }}
                navigation={props.navigation}
                visible={bundle.isAudit}
              />
              <Text style={ControlsStyleSheet.error}>{auditError}</Text>
              {isExternal ? (
                <>
                  <DynamicAutocomplete
                    label={'Submitted From'}
                    config={{optionSource: ListDataTypes.ORGANISATIONS}}
                    value={externalOrgValue}
                    showError={showErrors}
                    disabled={true}
                  />
                  <DynamicRecipients
                    label='Recipients'
                    config={{
                      externalOrgId: bundle.externalOrgId,
                    }}
                    value={
                      bundle.externalRecipients
                        ? JSON.stringify(bundle.externalRecipients)
                        : '[]'
                    }
                    showError={showErrors}
                    disabled={true}
                  />
                </>
              ) : (
                <>
                  <DynamicRecipients
                    label='Internal Recipients'
                    value={
                      bundle.internalRecipients
                        ? JSON.stringify(bundle.internalRecipients)
                        : '[]'
                    }
                    onChange={(controlId, controlTypeId, value, isValid) =>
                      onChange('internalRecipients', value, isValid)
                    }
                    showError={showErrors}
                    disabled={bundle.status === SubmissionStatuses.SUBMITTED}
                  />
                  {(canSubmitExternal ||
                    bundle.status === SubmissionStatuses.SUBMITTED) && (
                    <React.Fragment>
                      <DynamicAutocomplete
                        label={'External Organisation'}
                        config={{optionSource: ListDataTypes.ORGANISATIONS}}
                        value={externalOrgValue}
                        onChange={(controlId, controlTypeId, value, isValid) =>
                          onChange('externalOrgId', value, isValid)
                        }
                        showError={showErrors}
                        disabled={
                          bundle.status === SubmissionStatuses.SUBMITTED
                        }
                      />
                      {bundle.externalOrgId && (
                        <DynamicRecipients
                          label='External Recipients'
                          config={{
                            externalOrgId: bundle.externalOrgId,
                            required: true,
                          }}
                          value={
                            bundle.externalRecipients
                              ? JSON.stringify(bundle.externalRecipients)
                              : '[]'
                          }
                          onChange={(
                            controlId,
                            controlTypeId,
                            value,
                            isValid,
                          ) => onChange('externalRecipients', value, isValid)}
                          showError={showErrors}
                          disabled={
                            bundle.status === SubmissionStatuses.SUBMITTED
                          }
                        />
                      )}
                    </React.Fragment>
                  )}
                </>
              )}
              <Text style={ControlsStyleSheet.label}>ATTACHMENTS</Text>
              <View style={ControlsStyleSheet.bundleAttachmentsContainer}>
                <Pressable
                  style={({pressed}) => [
                    ControlsStyleSheet.bundleAttachmentsButton,
                    pressed && {
                      opacity: 0.6,
                    },
                  ]}
                  onPress={() => {
                    showFilePickerCommand.current.showFilePicker();
                  }}>
                  <Icon icon={'file'} color={Colors.white} size={24} />
                  <Text
                    style={[
                      ControlsStyleSheet.attachmentsText,
                      {color: Colors.white},
                    ]}>
                    Add From Device
                  </Text>
                </Pressable>
                <Pressable
                  style={({pressed}) => [
                    ControlsStyleSheet.bundleAttachmentsButton,
                    pressed && {
                      opacity: 0.6,
                    },
                  ]}
                  onPress={() => {
                    openDocumentLibraryCommand.current.openDocumentLibrary();
                  }}>
                  <Icon icon={'library'} color={Colors.white} size={24} />
                  <Text
                    style={[
                      ControlsStyleSheet.attachmentsText,
                      {color: Colors.white},
                    ]}>
                    Add From Document Library
                  </Text>
                </Pressable>
                <Pressable
                  style={({pressed}) => [
                    ControlsStyleSheet.bundleAttachmentsButton,
                    pressed && {
                      opacity: 0.6,
                    },
                  ]}
                  onPress={() => {
                    showDocumentsModalCommand.current.showDocumentsModal();
                  }}>
                  <Image
                    style={{width: 24, height: 24}}
                    source={Images.FIRS_LOGO_LIGHT_TREE}
                  />
                  <Text
                    style={[
                      ControlsStyleSheet.attachmentsText,
                      {color: Colors.white},
                    ]}>
                    Add From My FIRS Content
                  </Text>
                </Pressable>
              </View>
              <View
                style={[
                  ControlsStyleSheet.attachmentsContainer,
                  {paddingLeft: 0, paddingTop: 12},
                ]}>
                <DynamicAttachments
                  submissionId={SQLServerId}
                  attachmentsType='submission-bundle'
                  config={{
                    mode: 'photos-attachments',
                    allowedTypes: [
                      'application/pdf',
                      'image/png',
                      'image/jpg',
                      'image/jpeg',
                    ],
                    maxAttachments: 35,
                    showDocumentLibrary: true,
                  }}
                  value={bundle.bundleAttachments}
                  onChange={(controlId, controlTypeId, value, isValid) => {
                    onChange('attachments', value, isValid);
                  }}
                  setIsLoading={setIsLoading}
                  showError={showErrors}
                  disabled={bundle.status === SubmissionStatuses.SUBMITTED}
                  showButtons={false}
                  setShowFilePicker={(func: () => void) => {
                    showFilePickerCommand.current.setShowFilePicker(func);
                  }}
                  setOpenDocumentLibrary={(func: () => void) => {
                    openDocumentLibraryCommand.current.setOpenDocumentLibrary(
                      func,
                    );
                  }}
                  itemMaxWidth={320}
                />
                <DynamicBundleLinkRecord
                  navigation={props.navigation}
                  bundleId={props.bundleId!}
                  onChange={(controlId, controlTypeId, value, isValid) => {
                    onChange('submissions', value, isValid);
                  }}
                  showError={showErrors}
                  disabled={bundle.status === SubmissionStatuses.SUBMITTED}
                  showAddButton={false}
                  setShowModal={(func: () => void) => {
                    showDocumentsModalCommand.current.setShowDocumentsModal(
                      func,
                    );
                  }}
                />
              </View>
              {!isExternal && bundle.status === SubmissionStatuses.DRAFT && (
                <View style={{padding: 10}}>
                  <Pressable
                    style={CommonStyleSheet.greenButton}
                    android_ripple={{color: Colors.whiteTransparent}}
                    onPress={onSubmit}>
                    <Text style={CommonStyleSheet.greenButtonText}>Submit</Text>
                  </Pressable>
                </View>
              )}
            </React.Fragment>
          )}
          {submissionError ? (
            <Text style={[ControlsStyleSheet.error, {alignSelf: 'center'}]}>
              {submissionError}
            </Text>
          ) : null}
          {documentsError ? (
            <Text style={[ControlsStyleSheet.error, {alignSelf: 'center'}]}>
              {documentsError}
            </Text>
          ) : null}
          {isLoading && <LoadingSpinner message='Loading' visible={true} />}
        </View>
      </ScrollView>
      {renderActionsModal()}
      {renderConfirmModal()}
      {renderPDFModal()}
    </View>
  );
};

export default EditSubmissionBundle;
